/**
 * EPBCS Phase 3 — Capital Planning, Non-Project assets, Valid Combos, Review.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Input, Select, Btn } from "./common";
import { PovBar, useDims, fmtNum, ExportButton, ScenarioTh } from "./povUtils";
import { useCapexPov, capexPovFields } from "./CapexSection";
import { ItemComments, useCommentCounts } from "./ItemComments";

const useCapexData = (endpoint) => {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = () => {
    if (!pov) return;
    setError(null);
    api.post(endpoint, { pov })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e?.response?.data?.detail || e.message); });
  };
  useEffect(load, [pov]); // eslint-disable-line
  return { dims, pov, setPov, data, error, load };
};

// ------------------------------------------------- Capital planning --------
export const PlanningTab = () => {
  const { dims, pov, setPov, data, error } = useCapexData("/epbcs/capital-planning");
  const [filter, setFilter] = useState("all");
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="planning-error">{error}</p>;
  const rows = (data?.rows || []).filter((r) =>
    filter === "all" || (filter === "new" ? !r.is_existing : r.is_existing));
  return (
    <div className="space-y-4" data-testid="capex-planning-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PovBar testid="pov-capex_planning" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
        <div className="flex gap-1.5" data-testid="planning-filter">
          {[["all", "All"], ["new", "New Only"], ["existing", "Existing Only"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`planning-filter-${k}`}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded border transition-colors ${
                filter === k ? "border-[#0B1E3F] text-[#0B1E3F] bg-[#0B1E3F]/5" : "border-slate-300 text-slate-600"
              }`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500">
        Derived automatically: capitalisation date = latest task end date per project · straight-line
        depreciation = asset cost ÷ useful life, from the capitalisation month.
      </p>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3 min-w-[110px]">Project</th><th className="py-2 px-2 min-w-[130px]">Name</th>
                <th className="py-2 px-2">Status</th><th className="py-2 px-2">Category</th><th className="py-2 px-2">Capitalisation</th>
                <th className="py-2 px-2 text-right">Asset Cost</th><th className="py-2 px-2">Life</th>
                {data.months.map((m) => <ScenarioTh key={m} m={m} scenarioByMonth={data.scenario_by_month} testid={`scenario-badge-planning-${m}`} />)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`planning-row-${r.project_code}-${r.asset_category}`}>
                  <td className="py-1.5 px-3 font-mono font-semibold text-[#0B1E3F] whitespace-nowrap">{r.project_code}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{r.project_name}</td>
                  <td className="py-1.5 px-2">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      r.is_existing ? "bg-slate-100 text-slate-500" : "bg-green-50 text-green-700"}`}>
                      {r.is_existing ? "Existing" : "New"}
                    </span>
                  </td>
                  <td className="py-1.5 px-2">{r.asset_category}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap font-medium">{r.capitalisation_date || "—"}</td>
                  <td className="py-1.5 px-2 text-right font-semibold">{fmtNum(r.asset_cost)}</td>
                  <td className="py-1.5 px-2">{r.useful_life_months}m</td>
                  {data.months.map((m) => (
                    <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap ${r.depreciation?.[m] ? "text-[#0B1E3F] font-medium" : "text-slate-300"}`}>
                      {fmtNum(r.depreciation?.[m] || 0)}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7 + data.months.length} className="py-6 text-center text-slate-400">
                  No WBS projects with tasks match this filter yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------- Non-project assets ------
const EMPTY_ASSET = {
  division: "", description: "", purchase_date: "", supplier: "",
  asset_category: "", capitalization_start: "",
  units: "", rate: "", currency: "", salvage_value: "",
};

export const AssetsTab = ({ mode }) => {
  const isNew = mode === "new";
  const { dims, pov, setPov, data, error, load } = useCapexData("/epbcs/assets/list");
  const [draft, setDraft] = useState(null);
  const [combos, setCombos] = useState([]);
  const commentCounts = useCommentCounts("capex", pov);
  useEffect(() => { api.get("/epbcs/valid-combos").then((r) => setCombos(r.data)).catch(() => {}); }, []);
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="assets-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");
  const visibleAssets = (data?.assets || []).filter((a) => Boolean(a.is_existing) !== isNew);
  const validCatsFor = (div) => (dims.asset_category || []).filter((c) =>
    combos.some((v) => v.division === div && v.asset_category === c.code));

  const save = async () => {
    try {
      await api.post("/epbcs/assets", { pov, ...draft,
        units: +draft.units || 0, rate: +draft.rate || 0,
        salvage_value: +draft.salvage_value || 0 });
      toast.success("Asset purchase saved");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save asset");
    }
  };

  return (
    <div className="space-y-4" data-testid={`capex-assets-tab-${mode}`}>
      <p className="text-[11px] text-slate-500 -mb-1">
        {isNew
          ? "New asset purchases — direct procurement outside a WBS project."
          : "Assets already capitalised — locked from editing; shown read-only with their depreciation schedule."}
      </p>
      <PovBar testid={`pov-capex_assets_${mode}`} pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
      {isNew && canEdit && !draft && (
        <button onClick={() => { const div = dims.division?.[0]?.code || "";
          setDraft({ ...EMPTY_ASSET, division: div,
            asset_category: validCatsFor(div)[0]?.code || "",
            currency: dims.currency?.[0]?.code || "" }); }}
          data-testid="new-asset-btn"
          className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
          <Plus size={13} weight="bold" /> New asset purchase
        </button>
      )}
      {isNew && draft && (
        <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4" data-testid="asset-draft">
          <h4 className="font-bold text-sm mb-3">{draft.id ? "Edit asset" : "New asset"} <span className="text-[11px] font-normal text-slate-500">(code auto-generated: Division_FYyy_DP_Seq · cost = units × rate · useful life from category)</span></h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Division *">
              <Select value={draft.division} data-testid="asset-cc"
                onChange={(e) => setDraft({ ...draft, division: e.target.value,
                  asset_category: validCatsFor(e.target.value)[0]?.code || "" })}>
                {(dims.division || []).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </Select>
            </Field>
            <Field label="Description"><Input data-testid="asset-desc" value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
            <Field label="Purchase Date"><Input type="date" data-testid="asset-purchase" value={draft.purchase_date}
              onChange={(e) => setDraft({ ...draft, purchase_date: e.target.value })} /></Field>
            <Field label="Supplier"><Input value={draft.supplier}
              onChange={(e) => setDraft({ ...draft, supplier: e.target.value })} /></Field>
            <Field label="Asset Category (valid for this Division)">
              {validCatsFor(draft.division).length === 0 ? (
                <p className="text-[11px] text-amber-700 pt-1.5" data-testid="asset-no-combos">
                  No categories assigned to this division — configure Valid Combinations first.
                </p>
              ) : (
                <Select value={draft.asset_category} data-testid="asset-category"
                  onChange={(e) => setDraft({ ...draft, asset_category: e.target.value })}>
                  {validCatsFor(draft.division).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Capitalization Start *"><Input type="date" data-testid="asset-capstart" value={draft.capitalization_start}
              onChange={(e) => setDraft({ ...draft, capitalization_start: e.target.value })} /></Field>
            <Field label="Units * (whole number)"><Input type="number" min="1" step="1" data-testid="asset-units" value={draft.units}
              onChange={(e) => setDraft({ ...draft, units: e.target.value === "" ? "" : Math.max(0, Math.floor(+e.target.value || 0)) })} /></Field>
            <Field label="Rate *"><Input type="number" min="0" data-testid="asset-rate" value={draft.rate}
              onChange={(e) => setDraft({ ...draft, rate: e.target.value })} /></Field>
            <Field label="Currency *">
              <Select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                {(dims.currency || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </Select>
            </Field>
            <Field label="Salvage Value"><Input type="number" value={draft.salvage_value}
              onChange={(e) => setDraft({ ...draft, salvage_value: e.target.value })} /></Field>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={save} data-testid="asset-save"
              disabled={!draft.division || !draft.purchase_date || !draft.units || !draft.rate ||
                !draft.capitalization_start || !draft.currency}>
              {draft.id ? "Save changes" : "Create asset"}
            </Btn>
          </div>
        </div>
      )}
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : visibleAssets.length === 0 ? (
        <p className="text-sm text-slate-400 rounded-sm border border-slate-200 bg-white p-6 text-center" data-testid="assets-empty">
          {isNew ? `No new asset purchases for this POV yet.${canEdit ? " Create one above." : ""}`
                 : "No existing (already-capitalised) assets for this POV yet."}
        </p>
      ) : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Asset Code</th><th className="py-2 px-2 min-w-[120px]">Description</th>
                <th className="py-2 px-2">Purchase</th><th className="py-2 px-2">Supplier</th>
                <th className="py-2 px-2">Category</th><th className="py-2 px-2">Life</th>
                <th className="py-2 px-2">Cap. Start</th><th className="py-2 px-2 text-right">Units</th>
                <th className="py-2 px-2 text-right">Rate</th><th className="py-2 px-2 text-right">Salvage</th>
                <th className="py-2 px-2 text-right">Asset Cost</th>
                {data.months.map((m) => <ScenarioTh key={m} m={m} scenarioByMonth={data.scenario_by_month} testid={`scenario-badge-assets-${m}`} />)}
                {canEdit && isNew && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {visibleAssets.map((a) => (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`asset-row-${a.asset_code}`}>
                  <td className="py-1.5 px-3 font-mono font-semibold text-[#0B1E3F] whitespace-nowrap">{a.asset_code}
                    <span className="ml-1.5"><ItemComments section="capex" pov={pov} itemType="asset"
                      itemKey={a.asset_code} itemLabel={`Asset ${a.asset_code} — ${a.description || a.asset_category}`}
                      count={commentCounts.counts[a.asset_code] || 0} onChanged={commentCounts.refresh} /></span>
                    {!isNew && (
                      <span data-testid={`asset-locked-${a.asset_code}`}
                        className="ml-2 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border border-slate-300 bg-slate-100 text-slate-600">
                        Existing — locked
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{a.description}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{a.purchase_date}</td>
                  <td className="py-1.5 px-2">{a.supplier}</td>
                  <td className="py-1.5 px-2">{a.asset_category}</td>
                  <td className="py-1.5 px-2">{a.useful_life_months}m</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{a.capitalization_start}</td>
                  <td className="py-1.5 px-2 text-right">{fmtNum(a.units)}</td>
                  <td className="py-1.5 px-2 text-right">{fmtNum(a.rate)}</td>
                  <td className="py-1.5 px-2 text-right">{fmtNum(a.salvage_value)}</td>
                  <td className="py-1.5 px-2 text-right font-semibold" data-testid={`asset-cost-${a.asset_code}`}>{fmtNum(a.asset_cost)}</td>
                  {data.months.map((m) => (
                    <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap ${a.depreciation?.[m] ? "text-[#0B1E3F] font-medium" : "text-slate-300"}`}>
                      {fmtNum(a.depreciation?.[m] || 0)}
                    </td>
                  ))}
                  {canEdit && isNew && (
                    <td className="py-1.5 px-2 whitespace-nowrap">
                      <button onClick={() => setDraft({ ...EMPTY_ASSET, ...a })}
                        data-testid={`asset-edit-${a.asset_code}`} title="Edit asset"
                        className="mr-1.5 text-slate-400 hover:text-[#0B1E3F]"><PencilSimple size={13} /></button>
                      <button onClick={async () => {
                        try { await api.delete(`/epbcs/assets/${a.id}`); toast.success("Asset deleted"); load(); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Could not delete asset"); load(); }
                      }}
                        data-testid={`asset-delete-${a.asset_code}`}
                        className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------- Valid combinations ------
export const CombosTab = () => {
  const dims = useDims();
  const [combos, setCombos] = useState(null);
  const [error, setError] = useState(null);
  const load = () => api.get("/epbcs/valid-combos")
    .then((r) => setCombos(r.data))
    .catch((e) => setError(e?.response?.data?.detail || e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (error) return <p className="text-sm text-red-600" data-testid="combos-error">{error}</p>;
  if (!dims || !combos) return <p className="text-sm text-slate-400">Loading…</p>;

  const isValid = (div, cat) => combos.some((c) => c.division === div && c.asset_category === cat);
  const toggle = async (div, cat) => {
    await api.post("/epbcs/valid-combos", { division: div, asset_category: cat, valid: !isValid(div, cat) });
    load();
  };

  return (
    <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5" data-testid="capex-combos-tab">
      <h3 className="font-bold tracking-tight mb-1">Valid Combinations — Division × Asset Category</h3>
      <p className="text-[12px] text-slate-500 mb-4">
        Only ticked categories can be used on a division's WBS tasks and asset purchases.
        A division with no ticks is blocked from creating tasks or purchases entirely.
      </p>
      <table className="text-[12px]">
        <thead>
          <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-4">Division</th>
            {(dims.asset_category || []).map((c) => <th key={c.code} className="py-2 px-3 text-center">{c.code}</th>)}
          </tr>
        </thead>
        <tbody>
          {(dims.division || []).map((div) => (
            <tr key={div.code} className="border-b border-slate-100">
              <td className="py-2 pr-4 font-medium whitespace-nowrap">{div.code} — {div.name}</td>
              {(dims.asset_category || []).map((cat) => (
                <td key={cat.code} className="py-2 px-3 text-center">
                  <input type="checkbox" checked={isValid(div.code, cat.code)}
                    onChange={() => toggle(div.code, cat.code)}
                    data-testid={`combo-${div.code}-${cat.code}`}
                    className="w-4 h-4 accent-[#0B1E3F] cursor-pointer" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ------------------------------------------------------------ Review -------
export const ReviewTab = () => {
  const { dims, pov, setPov, data, error } = useCapexData("/epbcs/capex-review");
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="review-error">{error}</p>;

  const totals = { capex: {}, depreciation: {} };
  let totalCost = 0;
  (data?.rows || []).forEach((r) => {
    totalCost += r.total_cost || 0;
    ["capex", "depreciation"].forEach((k) =>
      Object.entries(r[k] || {}).forEach(([m, v]) => { totals[k][m] = (totals[k][m] || 0) + v; }));
  });

  return (
    <div className="space-y-4" data-testid="capex-review-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[320px]">
          <PovBar testid="pov-capex_review" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
        </div>
        <ExportButton report="capex_review" pov={pov} />
      </div>
      <p className="text-[11px] text-slate-500">
        Summarised at Project / Asset Purchase level — the Total column is the full asset cost,
        independent of how spend is allocated across months.
      </p>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Type</th><th className="py-2 px-2">Item</th>
                <th className="py-2 px-2">Division</th><th className="py-2 px-2">Category</th>
                <th className="py-2 px-2">Measure</th>
                <th className="py-2 px-2 text-right">Total Asset Cost</th>
                {data.months.map((m) => <ScenarioTh key={m} m={m} scenarioByMonth={data.scenario_by_month} testid={`scenario-badge-review-${m}`} />)}
              </tr>
            </thead>
            <tbody>
              {data.rows.flatMap((r) => ["capex", "depreciation"].map((k) => (
                <tr key={`${r.code}-${k}`} className="border-b border-slate-100"
                  data-testid={`review-row-${r.code}-${k}`}>
                  <td className="py-1.5 px-3">
                    {k === "capex" && (
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 border ${
                        r.source === "Project" ? "text-[#0B1E3F] border-[#0B1E3F]/30" : "text-[#1F6B4A] border-[#1F6B4A]/30"}`}>
                        {r.source}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 whitespace-nowrap">
                    {k === "capex" && (<>
                      <span className="font-mono font-semibold text-[#0B1E3F]">{r.code}</span>
                      {r.name && <span className="text-slate-500 ml-1.5">{r.name}</span>}
                    </>)}
                  </td>
                  <td className="py-1.5 px-2">{k === "capex" ? r.division : ""}</td>
                  <td className="py-1.5 px-2">{k === "capex" ? r.asset_category : ""}</td>
                  <td className={`py-1.5 px-2 text-[10px] font-bold uppercase ${k === "capex" ? "text-[#0B1E3F]" : "text-amber-700"}`}>
                    {k === "capex" ? "CapEx" : "Depreciation"}
                  </td>
                  <td className="py-1.5 px-2 text-right font-semibold" data-testid={k === "capex" ? `review-cost-${r.code}` : undefined}>
                    {k === "capex" ? fmtNum(r.total_cost) : ""}
                  </td>
                  {data.months.map((m) => (
                    <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap ${r[k]?.[m] ? "" : "text-slate-300"}`}>
                      {fmtNum(r[k]?.[m] || 0)}
                    </td>
                  ))}
                </tr>
              )))}
              {data.rows.length === 0 && (
                <tr><td colSpan={6 + data.months.length} className="py-6 text-center text-slate-400">
                  Nothing to review yet — add WBS tasks or asset purchases.
                </td></tr>
              )}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                {["capex", "depreciation"].map((k) => (
                  <tr key={k} className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                    <td className="py-2 px-3" colSpan={4}>{k === "capex" ? "Total CapEx" : "Total Depreciation"}</td>
                    <td />
                    <td className="py-2 px-2 text-right" data-testid={`review-total-${k}`}>
                      {k === "capex" ? fmtNum(totalCost) : ""}
                    </td>
                    {data.months.map((m) => <td key={m} className="py-2 px-2 text-right whitespace-nowrap">{fmtNum(totals[k][m] || 0)}</td>)}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};
